// import { useEffect } from 'react';
// import { useAppDispatch, useAppSelector } from '@store/hooks';
// import { GetGlChartOfAccountsDetailsList } from '@store/Finance/ChartAccounts/ChartAccountsDetailsSlice';
// import { useTranslation } from 'react-i18next';

// const useGetAccounts = () => {
//   const { i18n } = useTranslation();
//   const dispatch = useAppDispatch();

//   useEffect(() => {
//     if (ChartDetiles?.length === 0) {
//       dispatch(GetGlChartOfAccountsDetailsList());
//     }
//   }, []);
//   const { ChartDetiles, ChartAccountsDetails, loading, error } = useAppSelector(state => state.ChartAccountsDetails);

//   const maxLevel = ChartAccountsDetails.maxLevel;

//   const Accounts = ChartDetiles?.filter(f => f.accountlevel === maxLevel);

//   //___ Options _____

//   const OptionsAccounts = Accounts?.map(({ id, name, name2, accountNumber, accountParant }) => ({
//     value: id,
//     label: `${i18n.language === 'en' ? name : name2} | ${accountNumber}`,
//     accountParant: accountParant,
//     accountNumber: accountNumber,
//     accountShow: (
//       <div className='d-flex justify-content-between'>
//         <div>{i18n.language === 'en' ? name : name2}</div>
//         <div className='text-theme ms-2'>{accountNumber}</div>
//       </div>
//     )
//   }));

//   const OptionsAccountsStartWith5 = OptionsAccounts?.filter(f => f.accountNumber.startsWith('5'));

//   //_______
//   return {
//     ChartDetiles,
//     Accounts,
//     OptionsAccountsStartWith5,
//     loading,
//     error,
//     OptionsAccounts
//   };
// };

// export default useGetAccounts;

import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { GetGlChartOfAccountsDetailsList } from '@store/Finance/ChartAccounts/ChartAccountsDetailsSlice';
import type { ChartDetiles } from '@store/Finance/ChartAccounts/ChartAccountsModels';

export type AccountOption = {
  value: string;
  label: string;
  accountParant?: string | null;
  accountNumber: string;
  name: string;
  name2: string;
  accountShow: any;
};

type UseGetAccountsArgs = {
  autoFetch?: boolean;
  onlyLevel?: number | 'max'; // الافتراضي max
  startsWith?: string; // مثال: "5"
};

const useGetAccounts = (args: UseGetAccountsArgs = {}) => {
  const { autoFetch = true, onlyLevel = 'max', startsWith } = args;

  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const { ChartDetiles, ChartAccountsDetails, loading, error } = useAppSelector(state => state.ChartAccountsDetails);

  const maxLevel = ChartAccountsDetails?.maxLevel ?? 0;

  const refetch = useCallback(() => {
    dispatch(GetGlChartOfAccountsDetailsList());
  }, [dispatch]);

  useEffect(() => {
    if (!autoFetch) return;

    const empty = !ChartDetiles || ChartDetiles.length === 0;
    if (empty && !loading) {
      refetch();
    }
  }, [autoFetch, ChartDetiles?.length, loading, refetch]);

  const accounts = useMemo<ChartDetiles[]>(() => {
    if (!ChartDetiles?.length) return [];

    const level = onlyLevel === 'max' ? maxLevel : onlyLevel;
    if (!level) return ChartDetiles;

    return ChartDetiles.filter(x => x.accountlevel === level);
  }, [ChartDetiles, onlyLevel, maxLevel]);

  const options = useMemo<AccountOption[]>(() => {
    if (!accounts.length) return [];

    const isEn = i18n.language === 'en';
    return accounts.map(a => ({
      value: a.id,
      label: `${isEn ? a.name : a.name2} | ${a.accountNumber}`,
      accountParant: a.accountParant,
      accountNumber: a.accountNumber,
      name: a.name,
      name2: a.name2,
      accountShow: (
        <div className='d-flex justify-content-between'>
          <div>{isEn ? a.name : a.name2} </div>
          <div className='text-theme ms-2'>{a.accountNumber}</div>
        </div>
      )
    }));
  }, [accounts, i18n.language]);

  const filteredOptions = useMemo(() => {
    if (!startsWith) return options;
    return options.filter(o => (o.accountNumber ?? '').startsWith(startsWith));
  }, [options, startsWith]);

  // تستخدمها داخل react-select بدل تخزين JSX في الداتا
  const formatAccountOptionLabel = useCallback(
    (o: AccountOption) => (
      <div className='d-flex justify-content-between'>
        <div>{i18n.language === 'en' ? o.name : o.name2}</div>
        <div className='text-theme ms-2'>{o.accountNumber}</div>
      </div>
    ),
    [i18n.language]
  );

  return {
    ChartDetiles,
    ChartAccountsDetails,
    maxLevel,

    accounts,
    options,
    filteredOptions, // لو تمرر startsWith بيرجع المفلتر
    OptionsAccounts: options, // نفس اسمك القديم
    OptionsAccountsStartWith5: options.filter(o => (o.accountNumber ?? '').startsWith('5')),

    formatAccountOptionLabel,
    refetch,

    loading,
    error
  };
};

export default useGetAccounts;
